import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * 浏览器录音（`MediaRecorder`）—— 声音克隆页「现场录音」那一路。
 *
 * ## 为什么要有它
 *
 * 参考站的克隆流程核心是「**朗读一段文字**」，它的主按钮就是一颗麦克风。
 * 只有文件上传的话，用户得先在别处录好、导出、再回来传 —— 中间那两步
 * 与「念一段话」毫无关系，纯粹是摩擦。
 *
 * ## 录出来的东西**不是**上游能直接吃的格式
 *
 * Chrome 上 `MediaRecorder` 默认出 `audio/webm;codecs=opus`，而上游
 * `/v1/files/upload` 只收 mp3 / m4a / wav。**转码在服务端做**
 * （`backend/app/audio.py`）—— 这里只负责把浏览器给的那坨字节连同**真实
 * MIME 类型**交出去，别在浏览器里硬转（那要引一个 mp3 编码器）。
 *
 * ⚠️ 所以后缀是按**实际 MIME** 推的（webm / m4a / ogg），不是写死 `.mp3`：
 * 文件名撒谎会让后端跳过转码，把 webm 当 mp3 发给上游，然后收到一句
 * 看不懂的 `2013 invalid params`。后端另有一道按**容器**判的兜底。
 *
 * ## 三个必须处理的现实
 *
 * ① `navigator.mediaDevices` 只在**安全上下文**（https / localhost）存在。
 *    内网 http 打开时它是 `undefined`，直接 `getUserMedia` 会抛 TypeError。
 *    这里先探一次，把「浏览器不给用麦克风」说清楚，而不是抛个英文异常。
 * ② 用户可能**拒绝授权**（NotAllowedError）。那不是 bug，要给出可照做的提示。
 * ③ **离开页面要停**。录制中切页而流还开着，浏览器标签页上的红点会一直亮 ——
 *    用户以为一直在录。
 */

/** `idle` 没录 / `recording` 正在录 / `ready` 录完待提交。 */
export type RecorderPhase = 'idle' | 'recording' | 'ready'

export type Recorder = {
  phase: RecorderPhase
  /** 已录秒数。`ready` 时就是**整段的时长** —— 前端拿它卡「至少 10 秒」。 */
  seconds: number
  blob: Blob | null
  /** 按实际 MIME 推出的后缀（不含点），如 `webm`。上传时用它拼文件名。 */
  ext: string
  error: string | null
  /** 这台机器/这个上下文能不能录音。**不能时界面要藏起录音那条路**。 */
  supported: boolean
  start: () => Promise<void>
  stop: () => void
  reset: () => void
}

/** 按优先级挑一个浏览器支持的容器。mp4 排前面：Safari 只有它，且上游直接收。 */
const PREFERRED_MIME = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']

const EXT_BY_MIME: { test: RegExp; ext: string }[] = [
  { test: /mp4|m4a|aac/i, ext: 'm4a' },
  { test: /ogg|opus/i, ext: 'ogg' },
  { test: /wav/i, ext: 'wav' },
  { test: /webm/i, ext: 'webm' },
]

function pickMime(): string {
  const can = (t: string) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(t)
  return PREFERRED_MIME.find(can) ?? ''
}

function extOf(mime: string): string {
  return EXT_BY_MIME.find((m) => m.test.test(mime))?.ext ?? 'webm'
}

export function useRecorder(): Recorder {
  const [phase, setPhase] = useState<RecorderPhase>('idle')
  const [seconds, setSeconds] = useState(0)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [ext, setExt] = useState('webm')
  const [error, setError] = useState<string | null>(null)

  const recRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<number | null>(null)
  /** 卸载后 `onstop` 还会回调一次 —— 那时 setState 是无意义的（也会惊动 React） */
  const aliveRef = useRef(true)

  const supported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined'

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
  }, [])

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      clearTimer()
      // 录制中切页：先摘掉 onstop（不再需要那段数据），再停流，避免红点常亮
      if (recRef.current && recRef.current.state !== 'inactive') {
        recRef.current.onstop = null
        try {
          recRef.current.stop()
        } catch {
          /* 已经停了 */
        }
      }
      releaseStream()
    }
  }, [clearTimer, releaseStream])

  const start = useCallback(async () => {
    if (!supported) {
      setError('这个浏览器不支持录音，或者页面不是 https / localhost —— 请改用「上传文件」。')
      return
    }
    setError(null)
    setBlob(null)
    setSeconds(0)
    let stream: MediaStream
    try {
      // 开降噪与回声消除：克隆要的是**干净的人声**，环境底噪会被一起学走。
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
    } catch (e) {
      const name = e instanceof DOMException ? e.name : ''
      setError(
        name === 'NotAllowedError'
          ? '麦克风权限被拒绝了。请在浏览器地址栏左侧的权限里允许「麦克风」，或改用「上传文件」。'
          : name === 'NotFoundError'
            ? '没找到麦克风设备 —— 请检查是否插了耳麦，或改用「上传文件」。'
            : `打不开麦克风：${e instanceof Error ? e.message : String(e)}`,
      )
      return
    }
    if (!aliveRef.current) {
      stream.getTracks().forEach((t) => t.stop())
      return
    }
    streamRef.current = stream

    const mime = pickMime()
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
    recRef.current = rec
    chunksRef.current = []

    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
    }
    rec.onstop = () => {
      clearTimer()
      releaseStream()
      if (!aliveRef.current) return
      const type = rec.mimeType || mime || 'audio/webm'
      const parts = chunksRef.current
      chunksRef.current = []
      if (!parts.length) {
        setPhase('idle')
        setError('这一遍没录到声音 —— 检查一下麦克风，再试一次。')
        return
      }
      setExt(extOf(type))
      setBlob(new Blob(parts, { type }))
      setPhase('ready')
    }

    try {
      rec.start()
    } catch (e) {
      releaseStream()
      setError(`录音启动失败：${e instanceof Error ? e.message : String(e)}`)
      return
    }
    setPhase('recording')
    clearTimer()
    timerRef.current = window.setInterval(() => {
      if (aliveRef.current) setSeconds((s) => s + 1)
    }, 1000)
  }, [supported, clearTimer, releaseStream])

  const stop = useCallback(() => {
    const rec = recRef.current
    if (rec && rec.state !== 'inactive') rec.stop()
    else clearTimer()
  }, [clearTimer])

  const reset = useCallback(() => {
    clearTimer()
    recRef.current = null
    chunksRef.current = []
    setBlob(null)
    setSeconds(0)
    setPhase('idle')
    setError(null)
  }, [clearTimer])

  return { phase, seconds, blob, ext, error, supported, start, stop, reset }
}

/** 秒 → `mm:ss`。录音计时与时长展示共用，别在两处各写一遍。 */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  const m = Math.floor(s / 60)
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}
